/**
 * The cache writes that keep the reads in this folder current.
 *
 * They live together rather than beside the query each one writes to because a
 * single change fans out across several: deleting a team has to touch the team,
 * the lists, and every member who was in it. Actions call these after a write;
 * the queries call the `addOrUpdate*Cache` ones to seed from a read.
 */
import { QueryClient } from '@tanstack/react-query';
import {
    Company,
    CompanyUser,
    GetCompaniesResponseData,
    Invite,
    Team
} from '@/api';
import {
    FETCH_COMPANIES_QUERY_KEY,
    FETCH_TEAM_COUNTS_QUERY_KEY,
    FETCH_TEAM_QUERY_KEY,
    FETCH_TEAMS_PAGINATED_QUERY_KEY,
    FETCH_USER_COUNTS_QUERY_KEY,
    FETCH_USERS_PAGINATED_QUERY_KEY
} from './company-query-keys';
import { TEAM_FILTER_OPTIONS_QUERY_KEY } from '@/common/queries';

export function addOrUpdateCompanyCache(
    queryClient: QueryClient,
    company: Company
): void {
    queryClient.setQueryData<Company>(
        ['fetchCompany', company.id],
        () => company
    );
}

/**
 * The same as `addOrUpdateCompanyCache`, plus the list write and the refetch
 * that only a mutation warrants — use this one when a company has actually
 * changed, and the singular one when simply seeding the cache from a read.
 */

/**
 * The same as `addOrUpdateCompanyCache`, plus the list write and the refetch
 * that only a mutation warrants — use this one when a company has actually
 * changed, and the singular one when simply seeding the cache from a read.
 */
export function addOrUpdateAllCompaniesCache(
    queryClient: QueryClient,
    company: Company
): void {
    addOrUpdateCompanyCache(queryClient, company);

    let isNewCompany = false;

    queryClient.setQueryData<GetCompaniesResponseData>(
        [FETCH_COMPANIES_QUERY_KEY],
        (old) => {
            if (!old) {
                isNewCompany = true;
                return { companies: [company], myUsers: [] };
            }

            // Either replace company if existing or add new company
            let didUpdate = false;
            const companies = old.companies.map((oldCompany) => {
                if (oldCompany.id === company.id) {
                    didUpdate = true;
                    return company;
                }

                return oldCompany;
            });

            if (!didUpdate) {
                companies.push(company);
            }

            isNewCompany = !didUpdate;

            return { ...old, companies };
        }
    );

    // A company the user has just created or joined brings a company user of
    // their own with it, and that only ever arrives from the server. Plain
    // company updates (rename, image, settings) match an existing id and must
    // not refetch.
    if (isNewCompany) {
        void queryClient.invalidateQueries({
            queryKey: [FETCH_COMPANIES_QUERY_KEY]
        });
    }
}

/** Seeds the per-team cache from a read. */

export function addOrUpdateTeamCache(
    queryClient: QueryClient,
    team: Team
): void {
    queryClient.setQueryData<Team>([FETCH_TEAM_QUERY_KEY, team.id], () => team);
}

/**
 * The same as `addOrUpdateTeamCache`, plus the invalidations that only a
 * mutation warrants — use this one when a team has actually changed, and the
 * singular one when simply seeding the cache from a read.
 */

/**
 * The same as `addOrUpdateTeamCache`, plus the invalidations that only a
 * mutation warrants — use this one when a team has actually changed, and the
 * singular one when simply seeding the cache from a read.
 */
export function addOrUpdateAllTeamsCache(
    queryClient: QueryClient,
    team: Team
): void {
    addOrUpdateTeamCache(queryClient, team);

    invalidateTeamLists(queryClient);
}

/**
 * The paginated list and the picker options are windows onto a server-side
 * ordering, so a changed team can't be patched into them by hand — where it
 * lands depends on the search, filters and sort of every mounted query.
 */

/**
 * The paginated list and the picker options are windows onto a server-side
 * ordering, so a changed team can't be patched into them by hand — where it
 * lands depends on the search, filters and sort of every mounted query.
 */
function invalidateTeamLists(queryClient: QueryClient): void {
    void queryClient.invalidateQueries({
        queryKey: [FETCH_TEAMS_PAGINATED_QUERY_KEY]
    });

    void queryClient.invalidateQueries({
        queryKey: [TEAM_FILTER_OPTIONS_QUERY_KEY]
    });

    void queryClient.invalidateQueries({
        queryKey: [FETCH_TEAM_COUNTS_QUERY_KEY]
    });
}

/** Drops a deleted team, and every trace of it on the members who were in it. */

export function deleteTeamFromCache(
    queryClient: QueryClient,
    teamId: string
): void {
    // Remove team
    queryClient.removeQueries({
        queryKey: [FETCH_TEAM_QUERY_KEY, teamId]
    });

    invalidateTeamLists(queryClient);

    // Remove team from company users
    queryClient
        .getQueriesData<CompanyUser>({
            queryKey: ['fetchCompanyUser'],
            exact: false
        })
        .forEach(([key, companyUser]) => {
            if (companyUser) {
                queryClient.setQueryData<CompanyUser>(key, () => {
                    return {
                        ...companyUser,
                        teams: companyUser.teams.filter(
                            (team) => team.teamId !== teamId
                        )
                    };
                });
            }
        });

    // My own company user carries its own copy of my teams, and it backs every
    // team permission check.
    queryClient.setQueryData<GetCompaniesResponseData>(
        [FETCH_COMPANIES_QUERY_KEY],
        (old) => {
            if (!old) {
                return old;
            }

            return {
                ...old,
                myUsers: old.myUsers.map((user) => ({
                    ...user,
                    teams: user.teams.filter((team) => team.teamId !== teamId)
                }))
            };
        }
    );

    void queryClient.invalidateQueries({
        queryKey: [FETCH_USERS_PAGINATED_QUERY_KEY]
    });
}

export function addOrUpdateInviteCache(
    queryClient: QueryClient,
    invite: Invite
): void {
    queryClient.setQueryData<Invite>(['fetchInvites', invite.id], () => invite);
}

/** Drops an invite that has been answered, either way. */

export function deleteInviteFromCache(
    queryClient: QueryClient,
    inviteId: string
): void {
    queryClient.removeQueries({
        queryKey: ['fetchInvites', inviteId]
    });

    queryClient.setQueryData<CompanyUser[]>(['fetchInvites'], (oldInvites) => {
        if (!oldInvites) {
            return [];
        }

        return oldInvites.filter((invite) => invite.id !== inviteId);
    });
}

export function addOrUpdateCompanyUserCache(
    queryClient: QueryClient,
    user: CompanyUser
): void {
    queryClient.setQueryData<CompanyUser>(
        ['fetchCompanyUser', user.id],
        () => user
    );

    updateMyCompanyUserCache(queryClient, user);
}

/**
 * `GET /v3/companies` carries the caller's own company user in each company, and
 * that copy backs every permission check, the time settings and the account
 * pages. Any fresher copy of that record has to replace it, or those stay stale
 * until the next full page load.
 */

/**
 * `GET /v3/companies` carries the caller's own company user in each company, and
 * that copy backs every permission check, the time settings and the account
 * pages. Any fresher copy of that record has to replace it, or those stay stale
 * until the next full page load.
 */
function updateMyCompanyUserCache(
    queryClient: QueryClient,
    user: CompanyUser
): void {
    queryClient.setQueryData<GetCompaniesResponseData>(
        [FETCH_COMPANIES_QUERY_KEY],
        (old) => {
            if (!old) {
                return old;
            }

            // Matched on the company user id — `userId` is null for invited
            // users, so it can never be the key here.
            let didUpdate = false;
            const myUsers = old.myUsers.map((myUser) => {
                if (myUser.id === user.id) {
                    didUpdate = true;
                    return user;
                }

                return myUser;
            });

            // Returning the same reference when nothing matched keeps the many
            // calls that are about *other* users from notifying observers.
            return didUpdate ? { ...old, myUsers } : old;
        }
    );
}

/**
 * The same as `addOrUpdateCompanyUserCache`, plus the invalidations that only a
 * mutation warrants — use this one when a user has actually changed, and the
 * singular one when simply seeding the cache from a read.
 */

/**
 * The same as `addOrUpdateCompanyUserCache`, plus the invalidations that only a
 * mutation warrants — use this one when a user has actually changed, and the
 * singular one when simply seeding the cache from a read.
 */
export function addOrUpdateCompanyUsersCache(
    queryClient: QueryClient,
    user: CompanyUser
): void {
    addOrUpdateCompanyUserCache(queryClient, user);

    void queryClient.invalidateQueries({
        queryKey: [FETCH_USERS_PAGINATED_QUERY_KEY]
    });

    void queryClient.invalidateQueries({
        queryKey: [FETCH_USER_COUNTS_QUERY_KEY]
    });
}
